/**
 * KPIs do dashboard executivo/operacional (Task 10 / R2P) — regra transversal
 * **"mesmos registros, mesmo histórico"** (D-03).
 *
 * Invariantes deste módulo (é o coração da task, leia antes de mexer):
 *
 * 1. **Nenhum número é um total cego.** Todo KPI carrega `drillDown.recordIds`
 *    com os ids EXATOS dos registros que o compõem. A tela de drill-down não
 *    recalcula nada: ela chama `selectKpiRecords` — a MESMA função usada aqui.
 *    É isso que impede o dashboard e a lista de divergirem.
 * 2. **Fonte ausente nunca vira zero.** Um KPI cuja fonte de dados ainda não
 *    existe (garantias/RMA = Task 15) nasce `state: "awaiting_source"` com
 *    `value: null` e a nota explicando. Zero significaria "nenhuma garantia
 *    aberta", o que seria mentira.
 * 3. **Regra de domínio vem de `packages/*`.** Disponibilidade usa
 *    `consolidatedAvailability` de `packages/catalog` (nunca estoque bruto —
 *    D-11.5) e o resumo do command center usa `computeCommandCenterStats` de
 *    `packages/ui`, as mesmas funções que o restante do sistema usa.
 * 4. **Puro e sem I/O.** Nada aqui faz fetch, lê env ou toca o DOM: por isso o
 *    cálculo é testável sem navegador (`tests/plataforma-dashboard.test.ts`).
 */
import { consolidatedAvailability } from "../../../../packages/catalog/src/index.js";
import { activeReservationIndex, CRITICAL_STOCK_THRESHOLD } from "../catalog/availability.js";
import { computeCommandCenterStats } from "../../../../packages/ui/src/fixtures.js";
import {
  assertConversationListItem,
  type CommandCenterStats,
  type ConversationListItem
} from "../../../../packages/ui/src/types.js";
import type {
  PlatformCatalogEntry,
  PlatformCatalogProduct,
  PlatformConversation,
  PlatformNotification,
  PlatformOrder,
  PlatformRmaCase,
  PricingMarginRow,
  PricingSummaryResponse
} from "../api/types.js";

/**
 * Corte de criticidade da disponibilidade consolidada. A constante é definida
 * em `catalog/availability.ts` (regra de catálogo) e reexportada aqui para as
 * telas e os testes que já a importavam deste módulo — card, tela de estoque e
 * tela de disponibilidade usam o MESMO corte.
 */
export { CRITICAL_STOCK_THRESHOLD };

/**
 * Janela de SLA de primeira resposta humana, em minutos. Default operacional
 * (D-10.7): uma conversa aguardando humano há mais de 15 minutos é violação.
 * Quando a Plataforma tiver política de SLA configurável (Task 13), este valor
 * passa a vir de lá — o cálculo continua aqui.
 */
export const SLA_FIRST_RESPONSE_TARGET_MINUTES = 15;

export type KpiId =
  | "faturamento"
  | "pedidos"
  | "margem_estimada"
  | "pedidos_pendentes"
  | "falhas_sincronizacao"
  | "estoque_critico"
  | "conversas_aguardando"
  | "garantias_abertas"
  | "sla_atendimento";

export type KpiFormat = "currency" | "count" | "percent";

/** `live` = calculado de registros reais; `awaiting_source` = fonte ainda não existe. */
export type KpiState = "live" | "awaiting_source";

export type KpiAudience = "executivo" | "operacional" | "ambos";

export type DrillDownRecordKind = "order" | "margin_row" | "catalog_entry" | "conversation" | "notification" | "rma_case";

export interface KpiDrillDown {
  /** Rota do SPA que lista os registros-fonte (inclui o filtro do KPI). */
  route: string;
  label: string;
  recordKind: DrillDownRecordKind;
  /** Ids dos registros que compõem o número. Vazio = nada compõe (honesto). */
  recordIds: string[];
}

export interface Kpi {
  id: KpiId;
  label: string;
  /** `null` quando não há fonte ou não há base para calcular (ex.: SLA sem fila). */
  value: number | null;
  format: KpiFormat;
  state: KpiState;
  /** Explicação do número (ou da ausência dele). Todo KPI tem uma. */
  note: string;
  audience: KpiAudience;
  drillDown: KpiDrillDown;
}

/**
 * Estado de uma fonte do dataset:
 * - `live`: a rota respondeu e os registros são reais;
 * - `awaiting_contract`: a rota do contrato **ainda não existe neste ambiente**
 *   (hoje: conversas e pricing no Worker de produção — paridade Worker → Edge
 *   Functions é trabalho da Task 18 / contrato `/api/*`). Lista vazia com esta
 *   marca NÃO é "não há registros": é "a fonte não respondeu aqui".
 */
export type DashboardSourceState = "live" | "awaiting_contract";

export interface DashboardSources {
  conversations: DashboardSourceState;
  pricing: DashboardSourceState;
  /**
   * Catálogo em duas camadas (Task 11). Só as RESERVAS dependem dele no
   * dashboard: sem a fonte, a disponibilidade é exibida sem subtração e o KPI
   * de estoque crítico declara isso na nota.
   */
  platformCatalog: DashboardSourceState;
}

/**
 * Dataset que alimenta o dashboard. Cada campo é o payload real de uma rota do
 * contrato HTTP (ver `api/platform-client.ts`); `rmaCases: null` significa
 * "rota ainda não existe", não "lista vazia". `sources` diz, por fonte, se o
 * ambiente publicou o contrato — é o que impede ler "0 conversas aguardando"
 * quando o que houve foi rota ausente.
 */
export interface DashboardDataset {
  orders: PlatformOrder[];
  conversations: PlatformConversation[];
  catalog: PlatformCatalogEntry[];
  pricing: PricingSummaryResponse;
  notifications: PlatformNotification[];
  rmaCases: PlatformRmaCase[] | null;
  /**
   * Catálogo em duas camadas (Task 11); `null` = rota ainda não publicada no
   * ambiente. Entra no dataset do dashboard por causa das reservas ativas, que
   * descontam da disponibilidade consolidada (D-11.5).
   */
  platformCatalog: PlatformCatalogProduct[] | null;
  sources: DashboardSources;
}

/** Nota padrão da ausência por contrato pendente (mesmo texto em card e tela). */
export const AWAITING_CONTRACT_LABEL = "ainda não disponível neste ambiente";

/** `true` quando a fonte não foi publicada no ambiente em que o app roda. */
export function isSourceAwaitingContract(dataset: DashboardDataset, source: keyof DashboardSources): boolean {
  return dataset.sources[source] === "awaiting_contract";
}

// --- Seletores: fonte única de verdade de "quais registros compõem o KPI" ---

export function selectAllOrders(dataset: DashboardDataset): PlatformOrder[] {
  return dataset.orders;
}

export function selectPaidOrders(dataset: DashboardDataset): PlatformOrder[] {
  return dataset.orders.filter((order) => order.status === "paid");
}

/**
 * Pedido pendente = aguarda ação para seguir (pagamento, cotação de frete) ou
 * falhou no pagamento. Pedido pago não é pendente; cancelado/devolvido só
 * existe a partir da Task 12 (máquina de estados de `platform_orders`).
 */
export function selectPendingOrders(dataset: DashboardDataset): PlatformOrder[] {
  return dataset.orders.filter(
    (order) =>
      order.status === "awaiting_payment" ||
      order.status === "awaiting_shipping_quote" ||
      order.status === "payment_failed"
  );
}

/** Linhas de margem com margem calculável (custo sincronizado + preço vigente). */
export function selectMarginRows(dataset: DashboardDataset): PricingMarginRow[] {
  return dataset.pricing.rows.filter((row) => row.marginPct !== null);
}

/** Itens com disponibilidade consolidada no/abaixo do corte crítico. */
export function selectCriticalStock(dataset: DashboardDataset): PlatformCatalogEntry[] {
  const reservations = activeReservationIndex(dataset.platformCatalog);
  return dataset.catalog.filter((entry) => {
    const availability = availabilityOf(entry, reservations.get(entry.product.id) ?? 0);
    return availability !== null && availability <= CRITICAL_STOCK_THRESHOLD;
  });
}

/**
 * Disponibilidade consolidada do item. `null` quando não há oferta vigente
 * (sem oferta não há o que vender nem estoque crítico a reportar).
 *
 * `activeReservations` vem do catálogo em duas camadas (Task 11) via
 * `reservationsOf`. Sem a fonte publicada o desconto é 0 — o MESMO
 * comportamento de antes — e a nota do KPI declara a ausência; nunca inflamos
 * nem deflacionamos a disponibilidade sem saber.
 */
export function availabilityOf(entry: PlatformCatalogEntry, activeReservations: number = 0): number | null {
  if (!entry.offer) return null;
  return consolidatedAvailability(entry.offer.stock, activeReservations);
}

/**
 * Reservas ativas por produto, do catálogo em duas camadas. Fonte única usada
 * pelo KPI e pela tela de estoque: card e lista não podem subtrair coisas
 * diferentes.
 */
export function reservationsOf(dataset: DashboardDataset): Map<string, number> {
  return activeReservationIndex(dataset.platformCatalog);
}

/**
 * Conversa aguardando atendimento humano: já está em modo humano (takeover ou
 * canal humano) e a última mensagem é do cliente — ou seja, a bola está com a
 * equipe. Conversa em modo `ai` não está "aguardando humano".
 */
export function selectWaitingConversations(dataset: DashboardDataset): PlatformConversation[] {
  return dataset.conversations.filter((conversation) => {
    if (conversation.mode !== "human") return false;
    const last = conversation.messages.at(-1);
    return last?.sender === "customer";
  });
}

/**
 * Fila do SLA = **denominador** do indicador: todas as conversas aguardando
 * humano, dentro e fora da janela. É este conjunto que o drill-down do SLA
 * navega (cada linha classificada), para que numerador e denominador do
 * percentual sejam auditáveis na própria lista (D-10.19).
 */
export function selectSlaQueue(dataset: DashboardDataset): PlatformConversation[] {
  return selectWaitingConversations(dataset);
}

/** Conversas aguardando humano além da janela de SLA (violações = numerador invertido). */
export function selectSlaBreaches(dataset: DashboardDataset, now: Date = new Date()): PlatformConversation[] {
  return selectSlaQueue(dataset).filter((conversation) => classifySlaStatus(conversation, now) === "breached");
}

/** `within` = dentro da janela de SLA; `breached` = acima da janela. */
export type SlaStatus = "within" | "breached";

/**
 * Minutos de espera desde a última mensagem do cliente. `null` quando a
 * conversa não tem mensagem de cliente (não há espera a medir).
 */
export function slaWaitingMinutes(conversation: PlatformConversation, now: Date = new Date()): number | null {
  const waitingSince = lastCustomerMessageAt(conversation);
  if (!waitingSince) return null;
  const elapsedMs = now.getTime() - new Date(waitingSince).getTime();
  if (Number.isNaN(elapsedMs)) return null;
  return Math.max(0, Math.floor(elapsedMs / 60000));
}

/**
 * Classificação de uma conversa da fila. Sem tempo de espera medível a
 * conversa conta como dentro da janela (não inventamos violação).
 */
export function classifySlaStatus(conversation: PlatformConversation, now: Date = new Date()): SlaStatus {
  const minutes = slaWaitingMinutes(conversation, now);
  if (minutes === null) return "within";
  return minutes > SLA_FIRST_RESPONSE_TARGET_MINUTES ? "breached" : "within";
}

export interface SlaBreakdown {
  /** Denominador: toda a fila humana usada no cálculo. */
  queue: PlatformConversation[];
  within: PlatformConversation[];
  breached: PlatformConversation[];
  /** Percentual dentro da janela; `null` quando não há fila. */
  compliancePct: number | null;
}

/**
 * Numerador e denominador do SLA no MESMO lugar — a tela de drill-down e a
 * nota do card leem daqui, então não existe percentual sem população.
 */
export function slaBreakdown(dataset: DashboardDataset, now: Date = new Date()): SlaBreakdown {
  const queue = selectSlaQueue(dataset);
  const breached = queue.filter((conversation) => classifySlaStatus(conversation, now) === "breached");
  const within = queue.filter((conversation) => classifySlaStatus(conversation, now) === "within");
  return {
    queue,
    within,
    breached,
    compliancePct: queue.length ? Math.round((within.length / queue.length) * 10000) / 100 : null
  };
}

export function lastCustomerMessageAt(conversation: PlatformConversation): string | null {
  for (let index = conversation.messages.length - 1; index >= 0; index -= 1) {
    const message = conversation.messages[index];
    if (message.sender === "customer") return message.createdAt;
  }
  return null;
}

/**
 * Falhas de sincronização = notificações operacionais não lidas cujo tipo
 * aponta sincronização/integração (Bling, catálogo, pedidos). A fonte é o
 * admin-runtime (`/api/admin/notifications`), o MESMO registro que a tela de
 * sincronização lista. A Task 11 acrescenta as divergências item a item
 * (`catalog_divergences`, migration 0030) a esta mesma tela.
 */
export function selectSyncFailures(dataset: DashboardDataset): PlatformNotification[] {
  return dataset.notifications.filter(
    (notification) => !notification.read && isSyncFailureType(notification.type) && notification.severity !== "info"
  );
}

export function isSyncFailureType(type: string): boolean {
  const normalized = type.toLowerCase();
  return normalized.includes("sync") || normalized.includes("sincroniza") || normalized.includes("bling");
}

/** Garantias abertas. `null` = fonte (Task 15) ainda não existe. */
export function selectOpenWarranties(dataset: DashboardDataset): PlatformRmaCase[] | null {
  if (dataset.rmaCases === null) return null;
  return dataset.rmaCases.filter((rmaCase) => rmaCase.state !== "closed");
}

// --- Agregações ------------------------------------------------------------

export function revenueCentsOf(orders: PlatformOrder[]): number {
  return orders.reduce((sum, order) => sum + (order.totalCents ?? 0), 0);
}

export function averageMarginPct(rows: PricingMarginRow[]): number | null {
  if (!rows.length) return null;
  const total = rows.reduce((sum, row) => sum + (row.marginPct ?? 0), 0);
  return Math.round((total / rows.length) * 100) / 100;
}

/**
 * SLA de primeira resposta: percentual da fila humana dentro da janela.
 * Sem fila (`0` conversas aguardando) devolve `null` — 100% sem nenhum caso
 * seria um número bonito e vazio, não um indicador.
 */
export function slaCompliancePct(dataset: DashboardDataset, now: Date = new Date()): number | null {
  return slaBreakdown(dataset, now).compliancePct;
}

/**
 * Converte a conversa real do `ConversationCore` no contrato de UI já existente
 * (`ConversationListItem` de `packages/ui`) — a tela de inbox migra de fixture
 * para dado real SEM trocar de contrato, e a validação reusa
 * `assertConversationListItem`.
 */
export function toConversationListItem(conversation: PlatformConversation): ConversationListItem {
  const last = conversation.messages.at(-1);
  const waitingHuman = conversation.mode === "human" && last?.sender === "customer";
  const item: ConversationListItem = {
    id: conversation.id,
    customerId: conversation.customerId,
    customerName: conversation.displayName || conversation.customerId,
    channel: conversation.channel === "whatsapp" ? "whatsapp" : "webchat",
    status: waitingHuman ? "pending" : "open",
    lastMessagePreview: last?.text ?? "",
    lastMessageAt: last?.createdAt ?? conversation.updatedAt,
    leadScore: conversation.leadScore,
    assignedTo: conversation.mode === "human" ? "human" : "ai",
    unreadCount: waitingHuman ? 1 : 0
  };
  assertConversationListItem(item);
  return item;
}

/**
 * Resumo do Command Center sobre dados reais, usando a MESMA função pura que a
 * versão de fixture usava (`computeCommandCenterStats`). É literalmente a tela
 * de fixture migrada para dado real.
 */
export function buildCommandCenterStats(dataset: DashboardDataset): CommandCenterStats {
  const conversations = dataset.conversations.map(toConversationListItem);
  return computeCommandCenterStats(conversations, revenueCentsOf(selectPaidOrders(dataset)));
}

// --- KPIs + drill-down ----------------------------------------------------

/**
 * Registros que compõem um KPI. `available: false` significa que a FONTE não
 * existe ainda (não que a lista esteja vazia) — a tela de drill-down usa isso
 * para explicar o estado em vez de mostrar "nenhum registro".
 */
export interface KpiRecords {
  kind: DrillDownRecordKind;
  ids: string[];
  available: boolean;
}

/**
 * `_now` permanece na assinatura (e é repassado pelas telas de drill-down)
 * porque a seleção de registros é um contrato de ponto-no-tempo: desde que o
 * SLA passou a navegar para a fila inteira (D-10.19), nenhum seletor depende
 * do instante — mas um seletor futuro (ex.: pedidos atrasados) dependerá, e a
 * tela já entrega o mesmo instante usado no cálculo do card.
 */
export function selectKpiRecords(kpiId: KpiId, dataset: DashboardDataset, _now: Date = new Date()): KpiRecords {
  switch (kpiId) {
    case "faturamento":
      return { kind: "order", ids: selectPaidOrders(dataset).map((order) => order.id), available: true };
    case "pedidos":
      return { kind: "order", ids: selectAllOrders(dataset).map((order) => order.id), available: true };
    case "pedidos_pendentes":
      return { kind: "order", ids: selectPendingOrders(dataset).map((order) => order.id), available: true };
    // `available: false` quando a fonte não foi publicada no ambiente: a tela
    // de drill-down explica a ausência em vez de dizer "nenhum registro".
    case "margem_estimada":
      return {
        kind: "margin_row",
        ids: selectMarginRows(dataset).map((row) => row.productId),
        available: !isSourceAwaitingContract(dataset, "pricing")
      };
    case "estoque_critico":
      return { kind: "catalog_entry", ids: selectCriticalStock(dataset).map((entry) => entry.product.id), available: true };
    case "conversas_aguardando":
      return {
        kind: "conversation",
        ids: selectWaitingConversations(dataset).map((conversation) => conversation.id),
        available: !isSourceAwaitingContract(dataset, "conversations")
      };
    // SLA navega para a POPULAÇÃO do cálculo (toda a fila humana), não só para
    // as violações: só assim o operador audita numerador e denominador do
    // percentual na mesma lista. A classificação por linha é da tela (D-10.19).
    case "sla_atendimento":
      return {
        kind: "conversation",
        ids: selectSlaQueue(dataset).map((conversation) => conversation.id),
        available: !isSourceAwaitingContract(dataset, "conversations")
      };
    case "falhas_sincronizacao":
      return { kind: "notification", ids: selectSyncFailures(dataset).map((notification) => notification.id), available: true };
    case "garantias_abertas": {
      const open = selectOpenWarranties(dataset);
      return { kind: "rma_case", ids: open?.map((rmaCase) => rmaCase.id) ?? [], available: open !== null };
    }
  }
}

/** Rota de drill-down de cada KPI. Usada pelo card e pelas páginas de lista. */
export const KPI_DRILLDOWN_ROUTES: Record<KpiId, string> = {
  faturamento: "/pedidos?kpi=faturamento",
  pedidos: "/pedidos?kpi=pedidos",
  pedidos_pendentes: "/pedidos?kpi=pedidos_pendentes",
  margem_estimada: "/margens?kpi=margem_estimada",
  estoque_critico: "/estoque?kpi=estoque_critico",
  conversas_aguardando: "/inbox?kpi=conversas_aguardando",
  sla_atendimento: "/inbox?kpi=sla_atendimento",
  falhas_sincronizacao: "/sincronizacao?kpi=falhas_sincronizacao",
  garantias_abertas: "/garantias?kpi=garantias_abertas"
};

function brl(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * Monta os nove indicadores do brief 3.1 a partir do dataset real.
 * Ordem estável (o teste e a tela dependem dela).
 */
export function buildDashboardKpis(dataset: DashboardDataset, now: Date = new Date()): Kpi[] {
  const paid = selectPaidOrders(dataset);
  const pending = selectPendingOrders(dataset);
  const marginRows = selectMarginRows(dataset);
  const criticalStock = selectCriticalStock(dataset);
  const waiting = selectWaitingConversations(dataset);
  const slaPopulation = slaBreakdown(dataset, now);
  const syncFailures = selectSyncFailures(dataset);
  const warranties = selectOpenWarranties(dataset);
  const revenueCents = revenueCentsOf(paid);
  const margin = averageMarginPct(marginRows);
  const sla = slaPopulation.compliancePct;
  // Fonte não publicada no ambiente: o indicador declara a ausência em vez de
  // calcular sobre lista vazia (que leria como "nenhuma conversa aguardando").
  const conversationsPending = isSourceAwaitingContract(dataset, "conversations");
  const pricingPending = isSourceAwaitingContract(dataset, "pricing");

  const kpi = (id: KpiId, partial: Omit<Kpi, "id" | "drillDown"> & { drillLabel: string; recordKind: DrillDownRecordKind; recordIds: string[] }): Kpi => ({
    id,
    label: partial.label,
    value: partial.value,
    format: partial.format,
    state: partial.state,
    note: partial.note,
    audience: partial.audience,
    drillDown: {
      route: KPI_DRILLDOWN_ROUTES[id],
      label: partial.drillLabel,
      recordKind: partial.recordKind,
      recordIds: partial.recordIds
    }
  });

  return [
    kpi("faturamento", {
      label: "Faturamento",
      value: revenueCents,
      format: "currency",
      state: "live",
      audience: "executivo",
      note: paid.length
        ? `Soma de ${paid.length} pedido(s) pago(s): ${brl(revenueCents)}.`
        : "Nenhum pedido pago no período — faturamento zero é o estado real, não ausência de dado.",
      drillLabel: "Ver pedidos pagos",
      recordKind: "order",
      recordIds: paid.map((order) => order.id)
    }),
    kpi("pedidos", {
      label: "Pedidos",
      value: dataset.orders.length,
      format: "count",
      state: "live",
      audience: "ambos",
      note: `${dataset.orders.length} pedido(s) registrado(s), de todos os canais já integrados.`,
      drillLabel: "Ver todos os pedidos",
      recordKind: "order",
      recordIds: dataset.orders.map((order) => order.id)
    }),
    kpi("margem_estimada", {
      label: "Margem estimada",
      value: pricingPending ? null : margin,
      format: "percent",
      state: pricingPending ? "awaiting_source" : "live",
      audience: "executivo",
      note: pricingPending
        ? `Resumo de margem ${AWAITING_CONTRACT_LABEL}: a rota /api/pricing/summary existe no contrato e em desenvolvimento, mas ainda não no caminho de produção. Sem a fonte não exibimos margem.`
        : margin === null
          ? `Nenhum item com custo sincronizado (${dataset.pricing.summary.missingCost} sem custo): sem custo não calculamos margem.`
          : `Média de ${marginRows.length} item(ns) com custo sincronizado. Piso aprovado: ${dataset.pricing.summary.minMarginPct}%.`,
      drillLabel: "Ver margem por produto",
      recordKind: "margin_row",
      recordIds: marginRows.map((row) => row.productId)
    }),
    kpi("pedidos_pendentes", {
      label: "Pedidos pendentes",
      value: pending.length,
      format: "count",
      state: "live",
      audience: "operacional",
      note: pending.length
        ? "Pedidos aguardando pagamento, cotação de frete ou com pagamento recusado."
        : "Nenhum pedido pendente.",
      drillLabel: "Ver pedidos pendentes",
      recordKind: "order",
      recordIds: pending.map((order) => order.id)
    }),
    kpi("falhas_sincronizacao", {
      label: "Falhas de sincronização",
      value: syncFailures.length,
      format: "count",
      state: "live",
      audience: "operacional",
      note: syncFailures.length
        ? "Alertas de sincronização/integração não lidos. A Task 11 acrescenta aqui as divergências item a item."
        : "Nenhuma falha de sincronização aberta (alertas não lidos de integração).",
      drillLabel: "Ver alertas de sincronização",
      recordKind: "notification",
      recordIds: syncFailures.map((notification) => notification.id)
    }),
    kpi("estoque_critico", {
      label: "Estoque crítico",
      value: criticalStock.length,
      format: "count",
      state: "live",
      audience: "operacional",
      note: isSourceAwaitingContract(dataset, "platformCatalog")
        ? `Itens com disponibilidade consolidada <= ${CRITICAL_STOCK_THRESHOLD}. Fonte de reservas (catálogo em duas camadas) ${AWAITING_CONTRACT_LABEL}: a disponibilidade está sem desconto de reserva.`
        : `Itens com disponibilidade consolidada <= ${CRITICAL_STOCK_THRESHOLD} (estoque vendável menos reservas ativas).`,
      drillLabel: "Ver itens em estoque crítico",
      recordKind: "catalog_entry",
      recordIds: criticalStock.map((entry) => entry.product.id)
    }),
    kpi("conversas_aguardando", {
      label: "Conversas aguardando",
      value: conversationsPending ? null : waiting.length,
      format: "count",
      state: conversationsPending ? "awaiting_source" : "live",
      audience: "operacional",
      note: conversationsPending
        ? `Inbox ${AWAITING_CONTRACT_LABEL}: a rota /api/channels/conversations ainda não é servida no caminho de produção. Zero aqui diria, falsamente, que ninguém está esperando.`
        : waiting.length
          ? "Conversas em atendimento humano cuja última mensagem é do cliente."
          : "Nenhuma conversa aguardando resposta humana.",
      drillLabel: "Abrir inbox",
      recordKind: "conversation",
      recordIds: waiting.map((conversation) => conversation.id)
    }),
    kpi("garantias_abertas", {
      label: "Garantias abertas",
      value: warranties === null ? null : warranties.length,
      format: "count",
      state: warranties === null ? "awaiting_source" : "live",
      audience: "ambos",
      note:
        warranties === null
          ? "Fonte de garantias/RMA (tabela rma_cases, Task 15) ainda não publicada: sem dado, não exibimos zero."
          : "Casos de garantia/RMA em aberto.",
      drillLabel: "Ver casos de garantia",
      recordKind: "rma_case",
      recordIds: warranties?.map((rmaCase) => rmaCase.id) ?? []
    }),
    kpi("sla_atendimento", {
      label: "SLA de atendimento",
      value: conversationsPending ? null : sla,
      format: "percent",
      state: conversationsPending ? "awaiting_source" : "live",
      audience: "operacional",
      note: conversationsPending
        ? `Fila de atendimento ${AWAITING_CONTRACT_LABEL}: sem a lista de conversas não há população de SLA para medir.`
        : sla === null
          ? "Sem conversas na fila humana: não há SLA a medir neste instante."
          : `${slaPopulation.within.length} de ${slaPopulation.queue.length} conversa(s) dentro da janela de ${SLA_FIRST_RESPONSE_TARGET_MINUTES} min (${slaPopulation.breached.length} fora).`,
      // O drill-down leva à fila inteira (denominador) classificada linha a
      // linha, não apenas às violações: é o percentual auditável.
      drillLabel: "Ver a fila do SLA (dentro e fora)",
      recordKind: "conversation",
      recordIds: slaPopulation.queue.map((conversation) => conversation.id)
    })
  ];
}

export function kpiById(kpis: Kpi[], id: KpiId): Kpi {
  const found = kpis.find((item) => item.id === id);
  if (!found) throw new Error(`unknown_kpi:${id}`);
  return found;
}

/**
 * Resumo de margem neutro, usado quando a fonte não respondeu. Todos os
 * contadores em zero e `avgMarginPct: null` — quem lê o dataset descobre que
 * não há margem por `sources.pricing`, nunca por um número inventado.
 */
export function emptyPricingSummary(): PricingSummaryResponse {
  return {
    summary: { analyzed: 0, belowTarget: 0, missingCost: 0, pendingRecommendations: 0, avgMarginPct: null, minMarginPct: 0 },
    rows: []
  };
}

/** Dataset vazio — usado como estado inicial honesto (nada carregado ainda). */
export function emptyDashboardDataset(): DashboardDataset {
  return {
    orders: [],
    conversations: [],
    catalog: [],
    pricing: emptyPricingSummary(),
    notifications: [],
    rmaCases: null,
    platformCatalog: null,
    // Nada foi carregado: declarar `live` aqui faria o dashboard vazio parecer
    // "nenhum registro" em vez de "nada buscado ainda".
    sources: { conversations: "awaiting_contract", pricing: "awaiting_contract", platformCatalog: "awaiting_contract" }
  };
}
